import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useChineseLang } from '../../i18n/chinese'
import { initialOf, replyProblem, replyTooLong } from './discussionLogic'

export const TeacherTick = () => {
  const { t } = useTranslation('discussion')
  return (
    <span className='dc-tick'>
      <svg viewBox='0 0 16 16' aria-hidden='true'>
        <circle cx='8' cy='8' r='8' fill='#068466' />
        <path d='M4.6 8.3l2.2 2.2 4.6-4.8' fill='none' stroke='#ffffff' strokeWidth='1.7' strokeLinecap='round' strokeLinejoin='round' />
      </svg>
      {t('teacher')}
    </span>
  )
}

export const Avatar = ({ name, teacher }: { name: string; teacher?: boolean }) => (
  <span className={`dc-av${teacher ? ' teacher' : ''}`} aria-hidden='true'>
    {initialOf(name)}
  </span>
)

export const CloseIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' aria-hidden='true'>
    <path d='M6 6l12 12M18 6L6 18' />
  </svg>
)

export const BellIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
    <path d='M6 9a6 6 0 1 1 12 0c0 5 2 6.5 2 6.5H4S6 14 6 9' />
    <path d='M10 19a2 2 0 0 0 4 0' />
  </svg>
)

export const HeartIcon = ({ filled }: { filled: boolean }) => (
  <svg viewBox='0 0 24 24' fill={filled ? 'currentColor' : 'none'} stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
    <path d='M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z' />
  </svg>
)

/** Like button with its count. On the viewer's own writing it only shows the count. */
export const LikeButton = ({
  likes,
  liked,
  own,
  onToggle,
  what,
}: {
  likes: number
  liked: boolean
  own: boolean
  onToggle: () => void
  what: string
}) => {
  const { t } = useTranslation('discussion')
  if (own) {
    return likes > 0 ? (
      <span className='dc-like own'>
        <HeartIcon filled={false} />
        {likes}
      </span>
    ) : null
  }
  return (
    <button
      type='button'
      className={`dc-like${liked ? ' on' : ''}`}
      aria-pressed={liked}
      aria-label={
        likes
          ? t(liked ? 'like.unlikeLabelWithCount' : 'like.likeLabelWithCount', { what, count: likes })
          : t(liked ? 'like.unlikeLabel' : 'like.likeLabel', { what })
      }
      onClick={onToggle}
    >
      <HeartIcon filled={liked} />
      {likes > 0 ? likes : t('like.like')}
    </button>
  )
}

export interface MenuAction {
  label: string
  onSelect: () => void
}

/** The "more" button on an answer or reply: report, block, hide, delete. */
export const MoreMenu = ({ actions, label }: { actions: MenuAction[]; label: string }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])
  if (!actions.length) return null
  return (
    <div className='dc-menu' ref={ref}>
      <button type='button' className='dc-menu-btn' aria-label={label} aria-expanded={open} onClick={() => setOpen(!open)}>
        <svg viewBox='0 0 24 24' fill='currentColor' aria-hidden='true'>
          <circle cx='5' cy='12' r='1.8' />
          <circle cx='12' cy='12' r='1.8' />
          <circle cx='19' cy='12' r='1.8' />
        </svg>
      </button>
      {open && (
        <div className='dc-menu-list' role='menu'>
          {actions.map((a) => (
            <button
              key={a.label}
              type='button'
              role='menuitem'
              onClick={() => {
                setOpen(false)
                a.onSelect()
              }}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

interface ReplyComposerProps {
  toName: string
  value: string
  onChange: (value: string) => void
  onCancel: () => void
  onSend: () => void
  sending: boolean
  error: string | null
  autoFocus?: boolean
}

// Not copy: the reply box's hint is Chinese on purpose ("reply in Chinese to ..."), and the close mark.
const replyPlaceholder = (toName: string) => `用中文回复 ${toName}`
const CANCEL_MARK = '×'

/** The one reply box: pinned under the list, always saying who the reply goes to. */
export const ReplyComposer = ({ toName, value, onChange, onCancel, onSend, sending, error, autoFocus }: ReplyComposerProps) => {
  const { t } = useTranslation('discussion')
  const zhLang = useChineseLang()
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const composing = useRef(false)
  // Why Send was refused. Checked only on Send: while a pinyin keyboard composes, the box briefly
  // holds Latin letters, and complaining about those would stop learners mid-word.
  const [refused, setRefused] = useState<string | null>(null)
  useEffect(() => {
    if (autoFocus) inputRef.current?.focus()
  }, [autoFocus, toName])
  useEffect(() => setRefused(null), [value, toName])
  const tooLong = replyTooLong(value)
  const canSend = !!value.trim() && !tooLong && !sending
  const trySend = () => {
    const problem = replyProblem(value)
    if (problem) {
      setRefused(problem)
      return
    }
    onSend()
  }
  const message = tooLong || refused || error
  return (
    <div className='dc-compose'>
      <div className='dc-to'>
        {t('composer.replyingTo', { name: toName })}
        <button type='button' aria-label={t('composer.cancel')} onClick={onCancel}>
          {CANCEL_MARK}
        </button>
      </div>
      <div className='dc-compose-row'>
        <label htmlFor='dc-reply-input' className='dc-sr'>
          {t('composer.label', { name: toName })}
        </label>
        <textarea
          id='dc-reply-input'
          ref={inputRef}
          className='dc-input'
          lang={zhLang}
          rows={1}
          placeholder={replyPlaceholder(toName)}
          value={value}
          maxLength={400}
          onChange={(e) => onChange(e.target.value)}
          onCompositionStart={() => (composing.current = true)}
          onCompositionEnd={() => (composing.current = false)}
          onKeyDown={(e) => {
            // Enter sends; Shift+Enter makes a new line. Never while a Chinese IME is composing.
            if (e.key === 'Enter' && !e.shiftKey && !composing.current && !(e.nativeEvent as any).isComposing) {
              e.preventDefault()
              if (canSend) trySend()
            }
          }}
        />
        <button type='button' className='dc-send' disabled={!canSend} onClick={trySend}>
          {sending ? t('composer.sending') : t('composer.send')}
        </button>
      </div>
      {message && (
        <div className='dc-error' role='alert'>
          {message}
        </div>
      )}
    </div>
  )
}

const PHONE_QUERY = '(max-width: 640px)'

export const useIsPhone = () => {
  const [isPhone, setIsPhone] = useState(() => {
    try {
      return window.matchMedia(PHONE_QUERY).matches
    } catch {
      return false
    }
  })
  useEffect(() => {
    let mql: MediaQueryList
    try {
      mql = window.matchMedia(PHONE_QUERY)
    } catch {
      return
    }
    const onChange = () => setIsPhone(mql.matches)
    mql.addEventListener?.('change', onChange)
    return () => mql.removeEventListener?.('change', onChange)
  }, [])
  return isPhone
}

/** Tells the bell to refresh its count after something here marked notifications read. */
export const NOTIFICATIONS_CHANGED = 'maayot:notifications-changed'
export const announceNotificationsChanged = () => {
  try {
    window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED))
  } catch {
    /* no window events: the bell catches up on its next refresh */
  }
}
